import { useCallback, useEffect, useState } from "react";
import { Calendar as CalendarIcon, Plus, ThumbsUp, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../api";
import { Card, EmptyState, formatDate } from "../../ui";

export function AgendaTab({ isAdmin, isAssociate }) {
  const [items, setItems] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });
  const [designating, setDesignating] = useState(null); // agenda item
  const [selectedMeeting, setSelectedMeeting] = useState("");

  const load = useCallback(
    () =>
      api.get("/portal/agenda").then((r) => setItems(r.data)).catch((e) => { setItems([]); console.warn("Falha ao carregar pautas:", e); }),
    [],
  );

  const loadMeetings = useCallback(
    () =>
      api
        .get("/portal/meetings")
        .then((r) =>
          setMeetings(r.data.filter((m) => m.status !== "cancelada")),
        )
        .catch((e) => console.warn("Falha ao carregar reuniões:", e)),
    [],
  );

  useEffect(() => {
    load();
    if (isAdmin) loadMeetings();
  }, [isAdmin, load, loadMeetings]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/portal/agenda", form);
      toast.success("Pauta enviada!");
      setOpen(false);
      setForm({ title: "", description: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const vote = async (it) => {
    if (!isAssociate) return;
    try {
      await api.post(`/portal/agenda/${it.id}/vote`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const changeStatus = async (it, status, meeting_id = null) => {
    try {
      const payload = { status };
      if (meeting_id) payload.meeting_id = meeting_id;
      await api.patch(`/admin/agenda/${it.id}`, payload);
      load();
      if (isAdmin) loadMeetings();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openDesignate = (it) => {
    setDesignating(it);
    setSelectedMeeting(it.meeting_id || "");
  };

  const confirmDesignate = async () => {
    if (!selectedMeeting) {
      toast.error("Selecione uma reunião.");
      return;
    }
    await changeStatus(designating, "aprovada", selectedMeeting);
    setDesignating(null);
    toast.success("Pauta designada à reunião.");
  };

  return (
    <div>
      {isAssociate && (
        <div className="mb-4">
          <button
            data-testid="new-agenda-btn"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            <Plus size={14} /> Sugerir pauta
          </button>
        </div>
      )}

      {open && (
        <Card className="p-5 mb-5">
          <form data-testid="new-agenda-form" onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Título *
              </label>
              <input
                data-testid="agenda-title"
                required
                minLength={3}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Descrição *
              </label>
              <textarea
                data-testid="agenda-description"
                required
                minLength={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[100px]"
              />
            </div>
            <button
              data-testid="agenda-submit"
              className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
            >
              Enviar pauta
            </button>
          </form>
        </Card>
      )}

      {items === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Sem pautas sugeridas ainda"
          hint="Seja o primeiro a propor um tema para a próxima reunião."
        />
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`agenda-row-${it.id}`}
              className="bg-white border border-slate-200 p-5 rounded-md flex flex-col md:flex-row md:items-start gap-4"
            >
              <button
                data-testid={`agenda-vote-${it.id}`}
                onClick={() => isAssociate && vote(it)}
                disabled={!isAssociate}
                className={`flex flex-col items-center justify-center w-16 h-16 rounded-sm border ${
                  it.voted_by_me
                    ? "bg-[#002776] text-white border-[#002776]"
                    : "bg-slate-50 text-slate-700 border-slate-200 hover:border-[#002776]"
                } ${!isAssociate ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <ThumbsUp size={16} />
                <span className="font-serif-display text-lg leading-none mt-1">
                  {it.vote_count}
                </span>
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <StatusPill status={it.status} />
                  {it.meeting_title && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#002776]/30 bg-[#002776]/8 text-[#002776] rounded-sm text-[10px] uppercase tracking-wide">
                      <CalendarIcon size={11} /> {it.meeting_title}
                      {it.meeting_date && (
                        <span className="opacity-70">
                          · {formatDate(it.meeting_date)}
                        </span>
                      )}
                    </span>
                  )}
                  <span className="text-slate-500">
                    por {it.submitter_name} · {formatDate(it.submitted_at)}
                  </span>
                </div>
                <h3 className="mt-2 font-serif-display text-xl">{it.title}</h3>
                <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">
                  {it.description}
                </p>
                {isAdmin && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button
                      data-testid={`approve-${it.id}`}
                      onClick={() => openDesignate(it)}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wide border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    >
                      {it.meeting_id ? "Trocar reunião" : "Designar pauta"}
                    </button>
                    <button
                      data-testid={`archive-${it.id}`}
                      onClick={() => changeStatus(it, "arquivada")}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wide border border-slate-300 text-slate-600 hover:bg-slate-50"
                    >
                      Arquivar
                    </button>
                    <button
                      data-testid={`pending-${it.id}`}
                      onClick={() => changeStatus(it, "pendente")}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wide border border-amber-300 text-amber-700 hover:bg-amber-50"
                    >
                      Pendente
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {designating && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4">
          <div className="bg-white rounded-md w-full max-w-md p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                  Designar pauta
                </div>
                <h3 className="mt-1 font-serif-display text-xl leading-snug">
                  {designating.title}
                </h3>
              </div>
              <button
                onClick={() => setDesignating(null)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mt-5">
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Vincular à reunião *
              </label>
              {meetings.length === 0 ? (
                <div className="mt-2 p-4 border border-amber-200 bg-amber-50 text-amber-800 text-sm rounded-sm">
                  Nenhuma reunião agendada. Vá para a aba{" "}
                  <strong>Reuniões agendadas</strong> e crie uma reunião antes
                  de designar pautas.
                </div>
              ) : (
                <select
                  data-testid="designate-meeting-select"
                  value={selectedMeeting}
                  onChange={(e) => setSelectedMeeting(e.target.value)}
                  className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
                >
                  <option value="">— Selecione —</option>
                  {meetings.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.title} — {formatDate(m.meeting_date)}
                      {m.status === "realizada" ? " (realizada)" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <button
                onClick={() => setDesignating(null)}
                className="px-4 py-2 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
              >
                Cancelar
              </button>
              <button
                data-testid="designate-confirm"
                onClick={confirmDesignate}
                disabled={meetings.length === 0}
                className="px-5 py-2 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-50"
              >
                Designar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StatusPill = ({ status }) => {
  const map = {
    pendente: { c: "bg-amber-50 text-amber-700 border-amber-200", label: "pendente" },
    aprovada: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "pauta designada" },
    arquivada: { c: "bg-slate-100 text-slate-600 border-slate-200", label: "arquivada" },
  };
  const cfg = map[status] || {
    c: "bg-slate-100 text-slate-700 border-slate-200",
    label: status,
  };
  return (
    <span
      className={`px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-wide ${cfg.c}`}
    >
      {cfg.label}
    </span>
  );
};

